import React from 'react';
import api from '../services/api';
import './InvoiceAnalysisModule.css';

const CATEGORY_LABEL = {
  identifiers:        'Identifiers (GSTIN / PAN / CIN)',
  dates:              'Dates',
  arithmetic:         'Arithmetic & Tax',
  document_integrity: 'Document Integrity',
  tampering:          'Visual Tampering',
  pattern:            'Pattern / Duplicate',
  vendor_context:     'Vendor / Brand Context',
  handwritten:        'Handwritten / Sparse Bill',
  other:              'Other',
};

const CATEGORY_ORDER = [
  'identifiers', 'document_integrity', 'tampering', 'arithmetic',
  'dates', 'pattern', 'vendor_context', 'handwritten', 'other',
];

const SEVERITY_RANK = { CRITICAL: 0, HIGH: 1, MEDIUM: 2, LOW: 3 };


function SummaryBanner({ summary }) {
  if (!summary) return null;
  return (
    <div className="summary-banner">
      <div className="summary-banner-label">Executive Summary</div>
      <div className="summary-banner-text">{summary}</div>
    </div>
  );
}

function FindingsList({ findings }) {
  if (!findings || findings.length === 0) {
    return (
      <div className="section-card">
        <div className="section-title">Findings</div>
        <div className="empty-state">No findings were raised. The document presents as legitimate based on all checks performed.</div>
      </div>
    );
  }
  const grouped = {};
  findings.forEach((f) => {
    const cat = CATEGORY_LABEL[f.category] ? f.category : 'other';
    if (!grouped[cat]) grouped[cat] = [];
    grouped[cat].push(f);
  });

  return (
    <div className="section-card">
      <div className="section-title">
        Findings <span className="findings-count">{findings.length}</span>
      </div>
      {CATEGORY_ORDER.filter((c) => grouped[c]).map((cat) => (
        <div key={cat} className="findings-category">
          <div className="findings-category-title">{CATEGORY_LABEL[cat]}</div>
          {grouped[cat].map((f, i) => (
            <div key={i} className="finding-item">
              <div className="finding-item-head">
                <div className="finding-item-title">{f.title}</div>
              </div>
              {f.explanation && <div className="finding-item-explanation">{f.explanation}</div>}
              {f.evidence && (
                <div className="finding-item-evidence">
                  <span className="finding-evidence-label">Evidence:</span> {f.evidence}
                </div>
              )}
            </div>
          ))}
        </div>
      ))}
    </div>
  );
}

function KVRow({ k, v }) {
  if (v === undefined || v === null || v === '') return null;
  return (
    <div className="kv-row">
      <span className="kv-key">{k}</span>
      <span className="kv-val">{String(v)}</span>
    </div>
  );
}

function GstPanCard({ gstPan }) {
  if (!gstPan) return null;
  const gp = gstPan.gst_profile;
  const pp = gstPan.pan_profile;
  const cp = gstPan.cin_profile;
  if (!gp && !pp && !cp) return null;
  return (
    <div className="section-card">
      <div className="section-title">Identifiers</div>
      {gp && (
        <div className="evidence-block">
          <div className="evidence-block-head">
            <span>GSTIN</span>
            {gp.is_active !== null && gp.is_active !== undefined && (
              <span className={`mini-badge ${gp.is_active ? 'mini-badge-ok' : 'mini-badge-bad'}`}>
                {gp.is_active ? 'Active' : 'Inactive'}
              </span>
            )}
          </div>
          <KVRow k="GSTIN" v={gp.gstin} />
          <KVRow k="Format valid" v={gp.format_valid ? 'Yes' : 'No'} />
          <KVRow k="Legal name" v={gp.legal_name} />
          <KVRow k="Trade name" v={gp.trade_name} />
          <KVRow k="State" v={gp.state} />
          <KVRow k="Source" v={gp.verification_source} />
          {gp.fraud_flags && gp.fraud_flags.length > 0 && (
            <KVRow k="Flags" v={gp.fraud_flags.join(', ')} />
          )}
        </div>
      )}
      {pp && (
        <div className="evidence-block">
          <div className="evidence-block-head"><span>PAN</span></div>
          <KVRow k="PAN" v={`${pp.value}${pp.source === 'gstin_derived' ? ' (derived from GSTIN)' : ''}`} />
          <KVRow k="Format valid" v={pp.format_valid ? 'Yes' : 'No'} />
          <KVRow k="Entity type" v={pp.components?.entity_type} />
          {pp.issue_messages && pp.issue_messages.length > 0 && (
            <KVRow k="Notes" v={pp.issue_messages.join('; ')} />
          )}
        </div>
      )}
      {cp && (
        <div className="evidence-block">
          <div className="evidence-block-head"><span>CIN</span></div>
          <KVRow k="CIN" v={cp.value} />
          <KVRow k="Format valid" v={cp.format_valid ? 'Yes' : 'No'} />
          <KVRow k="Listing" v={cp.components?.listing} />
          <KVRow k="State" v={cp.components?.state_name || cp.components?.state_code} />
          <KVRow k="Category" v={cp.components?.category} />
          {cp.issue_messages && cp.issue_messages.length > 0 && (
            <KVRow k="Notes" v={cp.issue_messages.join('; ')} />
          )}
        </div>
      )}
    </div>
  );
}

function DocumentIntegrityCard({ docInt }) {
  if (!docInt) return null;
  const hasAny = docInt.ocr_method || docInt.pdf_creator || docInt.pdf_producer ||
    (docInt.raw_text_flags && docInt.raw_text_flags.length);
  if (!hasAny) return null;
  return (
    <div className="section-card">
      <div className="section-title">Document Integrity</div>
      <div className="evidence-block">
        <KVRow k="OCR method" v={docInt.ocr_method} />
        <KVRow k="Pages" v={docInt.page_count} />
        <KVRow k="Characters" v={docInt.char_count} />
        <KVRow k="PDF creator" v={docInt.pdf_creator} />
        <KVRow k="PDF producer" v={docInt.pdf_producer} />
        <KVRow k="Created" v={docInt.pdf_created} />
        <KVRow k="Modified" v={docInt.pdf_modified} />
        {docInt.raw_text_flags && docInt.raw_text_flags.length > 0 && (
          <KVRow k="Raw-text flags" v={docInt.raw_text_flags.join(', ')} />
        )}
      </div>
    </div>
  );
}

function TemplateCard({ template }) {
  if (!template) return null;
  return (
    <div className="section-card">
      <div className="section-title">
        Template Match
        <span className="section-subtitle">(informational)</span>
      </div>
      <div className="evidence-block">
        <KVRow k="Score" v={template.score} />
        <KVRow k="Matched" v={template.is_match ? 'Yes' : 'No'} />
        <KVRow k="Vendor" v={template.vendor} />
        <KVRow k="Match type" v={template.match_type} />
        {template.message && <KVRow k="Note" v={template.message} />}
      </div>
    </div>
  );
}

function DuplicateAndHandwrittenCards({ deterministic }) {
  if (!deterministic) return null;
  const dup = deterministic.duplicate;
  const hw = deterministic.handwritten;
  const showDup = dup && dup.flags && dup.flags.length > 0;
  const showHw = hw && hw.details && hw.details.active;
  if (!showDup && !showHw) return null;
  return (
    <>
      {showDup && (
        <div className="section-card">
          <div className="section-title">Duplicate Detection</div>
          <div className="evidence-block">
            <KVRow k="Flags" v={dup.flags.join(', ')} />
          </div>
        </div>
      )}
      {showHw && (
        <div className="section-card">
          <div className="section-title">Handwritten / Sparse Bill</div>
          <div className="evidence-block">
            <KVRow k="Active" v="Yes" />
            {hw.flags && hw.flags.length > 0 && <KVRow k="Flags" v={hw.flags.join(', ')} />}
            {hw.details?.amount != null && <KVRow k="Detected amount" v={hw.details.amount} />}
          </div>
        </div>
      )}
    </>
  );
}

function ResultDashboard({ data }) {
  const { result, filename } = data;
  if (!result || result.error) {
    return (
      <div className="error-card">
        <div className="error-icon">⚠</div>
        <div>
          <div className="error-title">Analysis failed</div>
          <div className="error-msg">{result?.error || 'Unknown error'}</div>
          {result?.detail && <div className="error-detail">{result.detail}</div>}
        </div>
      </div>
    );
  }

  const summary = result.summary;
  const findings = result.findings || [];
  const gstPan = result.gst_pan;
  const docInt = result.document_integrity;
  const deterministic = result.deterministic_evidence || {};
  const template = result.template_details || deterministic.template;

  return (
    <div className="result-wrap">
      <div className="result-header">
        <div className="result-header-left">
          <div className="result-filename">{filename}</div>
          {result.ai_error && (
            <div className="ai-error-note">AI note: {result.ai_error}</div>
          )}
        </div>
      </div>

      <SummaryBanner summary={summary} />

      <FindingsList findings={findings} />

      <GstPanCard gstPan={gstPan} />
      <DocumentIntegrityCard docInt={docInt} />
      <DuplicateAndHandwrittenCards deterministic={deterministic} />
      {template && template.is_match && <TemplateCard template={template} />}
    </div>
  );
}

function ReportDownload({ displayResult }) {
  const [busy, setBusy] = React.useState(null);
  const [error, setError] = React.useState(null);

  const handleDownload = async (fmt) => {
    setBusy(fmt);
    setError(null);
    try {
      const fd = new FormData();
      fd.append('fmt', fmt);
      fd.append('analysis_json', JSON.stringify(displayResult));
      const resp = await api.post('/generate-report', fd, { responseType: 'blob' });
      const blob = new Blob([resp.data], {
        type: fmt === 'pdf'
          ? 'application/pdf'
          : 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
      });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      const base = (displayResult.filename || 'invoice').replace(/\.[^.]+$/, '');
      a.href = url;
      a.download = `${base}_fraud_report.${fmt}`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      setTimeout(() => URL.revokeObjectURL(url), 1000);
    } catch (err) {
      setError(err?.response?.data?.detail || err.message || 'Download failed');
    } finally {
      setBusy(null);
    }
  };

  return (
    <div className="report-download">
      <div className="report-download-label">Download fraud report</div>
      <div className="report-download-buttons">
        <button className="report-btn report-btn-pdf" onClick={() => handleDownload('pdf')} disabled={busy !== null}>
          {busy === 'pdf' ? (<><span className="spinner spinner-sm"></span>Generating PDF…</>) : (<>⬇ PDF</>)}
        </button>
        <button className="report-btn report-btn-docx" onClick={() => handleDownload('docx')} disabled={busy !== null}>
          {busy === 'docx' ? (<><span className="spinner spinner-sm"></span>Generating DOCX…</>) : (<>⬇ DOCX</>)}
        </button>
      </div>
      {error && <div className="report-download-error">⚠ {error}</div>}
    </div>
  );
}

export default function InvoiceAnalysisModule({
  displayResult,
  file,
  onFileChange,
  preview,
  onPreviewChange,
  dragging,
  onDraggingChange,
  loading,
  progress,
  textMode,
  onTextModeChange,
  invoiceText,
  onInvoiceTextChange,
  metadataNotes,
  onMetadataNamesChange,
  onAnalyze,
}) {
  const fileInputRef = React.useRef();

  const handleFile = (f) => {
    if (!f) return;
    onFileChange(f);
    if (f.type.startsWith('image/')) {
      const reader = new FileReader();
      reader.onload = (e) => onPreviewChange(e.target.result);
      reader.readAsDataURL(f);
    } else {
      onPreviewChange(null);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    onDraggingChange(false);
    handleFile(e.dataTransfer.files[0]);
  };

  return (
    <div className="content">
      <div className="left-panel">
        <div className="mode-toggle">
          <button className={`mode-btn ${!textMode ? 'active' : ''}`} onClick={() => onTextModeChange(false)}>
            Upload File
          </button>
          <button className={`mode-btn ${textMode ? 'active' : ''}`} onClick={() => onTextModeChange(true)}>
            Paste Text
          </button>
        </div>

        {!textMode ? (
          <>
            <div
              className={`dropzone ${dragging ? 'dragging' : ''} ${file ? 'has-file' : ''}`}
              onDragOver={e => { e.preventDefault(); onDraggingChange(true); }}
              onDragLeave={() => onDraggingChange(false)}
              onDrop={handleDrop}
              onClick={() => fileInputRef.current?.click()}
            >
              {file ? (
                <div className="file-info">
                  {preview ? (
                    <img src={preview} alt="preview" className="file-preview" />
                  ) : (
                    <div className="file-icon-big">PDF</div>
                  )}
                  <div className="file-name">{file.name}</div>
                  <div className="file-size">{(file.size / 1024).toFixed(1)} KB</div>
                  <div className="change-file">Click to change</div>
                </div>
              ) : (
                <div className="drop-prompt">
                  <div className="drop-icon">⬆</div>
                  <div className="drop-title">Drop invoice here</div>
                  <div className="drop-sub">PDF, PNG, JPG, JPEG, TIFF</div>
                </div>
              )}
            </div>
            <input
              ref={fileInputRef}
              type="file"
              accept=".pdf,.png,.jpg,.jpeg,.tiff,.bmp,.webp"
              style={{ display: 'none' }}
              onChange={e => handleFile(e.target.files[0])}
            />
          </>
        ) : (
          <div className="text-input-area">
            <div className="input-label">Invoice text</div>
            <textarea
              className="text-invoice"
              value={invoiceText}
              onChange={e => onInvoiceTextChange(e.target.value)}
              placeholder="Paste complete invoice text here&#10;&#10;Include: vendor name, GSTIN, PAN, invoice number, date, line items, amounts, totals..."
              rows={12}
            />
            <div className="input-label" style={{ marginTop: 12 }}>Metadata notes (optional)</div>
            <textarea
              className="text-meta"
              value={metadataNotes}
              onChange={e => onMetadataNamesChange(e.target.value)}
              placeholder="e.g. Created with Canva, received via email, date mismatch noticed..."
              rows={3}
            />
          </div>
        )}

        <button
          className={`analyze-btn ${loading ? 'loading' : ''}`}
          onClick={onAnalyze}
          disabled={loading || (textMode ? !invoiceText.trim() : !file)}
        >
          {loading ? (
            <><span className="spinner"></span>Analyzing… {Math.round(progress)}%</>
          ) : (
            <><span className="btn-icon">⬡</span>Run fraud analysis</>
          )}
        </button>

        {loading && (
          <div className="progress-bar">
            <div className="progress-fill" style={{ width: `${progress}%` }}></div>
          </div>
        )}

        {loading && (
          <div className="loading-steps">
            {['Extracting text', 'Gathering deterministic evidence', 'AI forensic analysis', 'Compiling findings'].map((step, i) => (
              <div key={i} className={`load-step ${progress > i * 25 ? 'done' : ''}`}>
                <span className="load-dot"></span>{step}
              </div>
            ))}
          </div>
        )}

        {displayResult && displayResult.result && !displayResult.result.error && !loading && (
          <ReportDownload displayResult={displayResult} />
        )}

        <div className="info-cards">
          {[
            { label: 'Identifiers', desc: 'GSTIN live + PAN/CIN syntax' },
            { label: 'Integrity',  desc: 'Raw-text + PDF metadata' },
            { label: 'Duplicates', desc: 'Registry-based de-dupe' },
            { label: 'AI verdict', desc: 'Exhaustive LLM analysis' },
          ].map(c => (
            <div key={c.label} className="info-card">
              <div className="info-label">{c.label}</div>
              <div className="info-desc">{c.desc}</div>
            </div>
          ))}
        </div>
      </div>

      <div className="right-panel">
        {displayResult ? (
          <ResultDashboard data={displayResult} />
        ) : (
          <div className="empty-result">
            <div className="empty-icon">⬡</div>
            <div className="empty-title">No analysis yet</div>
            <div className="empty-sub">Upload an invoice or paste text and run the analysis to see the AI forensic findings here.</div>
            <div className="empty-features">
              {[
                'GSTIN 15-char live verification',
                'PAN + CIN syntactic profiles',
                'Raw PDF text + invisible-character scan',
                'AI-driven exhaustive findings list',
                'PDF + DOCX downloadable report',
              ].map(f => (
                <div key={f} className="empty-feature">
                  <span style={{ color: '#6c63ff' }}>→</span> {f}
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
